import { format, getDaysInMonth } from 'date-fns'
import {
	combineName,
	easyData,
	finalInactiveSensors,
} from '../../../shared/format'
import { showTargetValue } from '../../../helper/ShowData'
import ALARM_STATES, { IO_STATUS } from './alarmSettings'
import prioritizeAlarms from './prioritizeAlarms'

const getIoModuleName = device => device.filter || 'Alarm-Modul'
const IO_OFFLINE_DELAY_MINUTES = 60

const isMissingIoStatusPending = (day, hourKey) => {
	const hour = parseInt(hourKey.split('_')[1], 10)
	const hourDate = new Date(`${day}T${hour.toString().padStart(2, '0')}:00:00`)
	const minutesSinceHourStarted =
		(new Date().getTime() - hourDate.getTime()) / 1000 / 60

	return minutesSinceHourStarted < IO_OFFLINE_DELAY_MINUTES
}

const generateRows = (devicesTimeRange, systemJson) => {
	const { timeRange } = devicesTimeRange
	const devices = devicesTimeRange.devices || []
	const { startDate, months } = timeRange
	let { days } = timeRange
	const allRows = []

	if (months) days = getDaysInMonth(startDate)

	devices.forEach(device => {
		const { attributes, id, serial } = device

		for (let i = 0; i < days; i++) {
			const values = {}

			for (let hour = 0; hour <= 23; hour++) {
				values[`hour_${hour}`] = device.isIoModule
					? { alarm: null, temp: null, ioStatus: null }
					: { alarm: null, temp: null }
			}

			const currentDay = new Date(startDate)
			currentDay.setDate(startDate.getDate() + i)

			allRows.push({
				day: format(currentDay, 'yyyy-MM-dd'),
				values,
				device: {
					name: device.isIoModule
						? getIoModuleName(device)
						: combineName(attributes),
					id,
					serial,
					battery: parseInt(attributes.battery),
					attributes,
					installation_place: attributes.installation_place,
					inactive: device.isIoModule
						? false
						: finalInactiveSensors(systemJson.temperatureThresholds, attributes),
					alarm_active: attributes.alarm_active,
					isIoModule: device.isIoModule,
					last_timestamp: attributes.last_timestamp,
					target: device.isIoModule
						? ''
						: showTargetValue(
								systemJson.temperatureThresholds.find(
									({ place }) => place === attributes.installation_place
								)
							),
				},
			})
		}
	})

	return allRows
}

const filterDataByState = (data, state) => {
	const dataWithZero = easyData(data.filter(d => d.measuredValue == state))
	const dataFilterZero = dataWithZero.filter(entry => entry.value !== '0')
	return dataFilterZero
}

const prepareTemperature = (
	data,
	temperatureData,
	ioStatusData,
	devicesTimeRange,
	systemJson
) => {
	const defrostData = filterDataByState(data, ALARM_STATES.DEFROST)
	const warmingData = filterDataByState(data, ALARM_STATES.WARMUP)
	const warningData = filterDataByState(data, ALARM_STATES.WARNING)
	const alarmsData = filterDataByState(data, ALARM_STATES.ALARM)
	const processedTempData = easyData(temperatureData)
	const processedIoStatusData = easyData(
		ioStatusData.filter(d => d.measuredValue === ALARM_STATES.ALARM)
	)

	const allRows = generateRows(devicesTimeRange, systemJson)

	allRows.forEach(row => {
		const filterRow = entry =>
			entry?.day === row?.day && entry.deviceId === row.device.id

		const currentRowData = {
			[ALARM_STATES.DEFROST]: defrostData.filter(filterRow),
			[ALARM_STATES.WARMUP]: warmingData.filter(filterRow),
			[ALARM_STATES.WARNING]: warningData.filter(filterRow),
			[ALARM_STATES.ALARM]: alarmsData.filter(filterRow),
			ioStatus: processedIoStatusData.filter(filterRow),
			temp: processedTempData.filter(filterRow),
		}
		const alarmStates = [
			ALARM_STATES.ALARM,
			ALARM_STATES.WARNING,
			ALARM_STATES.DEFROST,
			ALARM_STATES.WARMUP,
		]

		const getHourValue = (type, hour) => {
			const entry = currentRowData[type].find(
				entry => `hour_${entry.hour}` === hour
			)
			return entry ? entry.value : '0'
		}

		for (const hour in row.values) {
			if (row.device.isIoModule) {
				const hourIoValue = currentRowData.ioStatus.find(
					entry => `hour_${entry.hour}` === hour
				)?.value

				if (hourIoValue === '1') {
					row.values[hour].ioStatus = IO_STATUS.ALARM
				} else if (hourIoValue === '0') {
					row.values[hour].ioStatus = IO_STATUS.FUNCTIONAL
				} else {
					row.values[hour].ioStatus = hourIoValue
						? IO_STATUS.FUNCTIONAL
						: isMissingIoStatusPending(row.day, hour)
							? null
							: IO_STATUS.OFFLINE
				}
				continue
			}

			const measurements = alarmStates.map(state => ({
				measuredValue: state,
				value: getHourValue(state, hour),
			}))

			row.values[hour].alarm = prioritizeAlarms(measurements)

			const hourTempValue = currentRowData.temp.find(
				entry => `hour_${entry.hour}` === hour
			)?.value
			if (hourTempValue) {
				row.values[hour].temp = Number(hourTempValue).toFixed(1)
			}
		}
	})

	return [...allRows].sort((a, b) => {
		if (a.device.isIoModule !== b.device.isIoModule) {
			return a.device.isIoModule ? -1 : 1
		}
		if (a.device.isIoModule) {
			return a.device.name.localeCompare(b.device.name)
		}
		if (a.device.inactive !== b.device.inactive) {
			return a.device.inactive - b.device.inactive
		}
		return a.device.name.localeCompare(b.device.name)
	})
}

export default prepareTemperature
